import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { shallow } from 'zustand/shallow';

import { Button, Fade, Icon } from '@column/column-ui-kit';
import { DataTable, type DataTableColumnDef } from '@column/ui';

import { EventTypeCell, JSONButton } from '../Events';
import { ROUTE } from '~/app/routes';
import { Loading, SectionHeader } from '~/components';
import { EventDetailModal } from '~/components/Dialog';
import { Empty as TableEmpty, getTimestampColumn, tableDefaults, useTimeFormat } from '~/components/Table';
import { LogoLoading } from '~/elements';
import { useNavigate } from '~/lib/navigation';
import {
  EntityRepository,
  EventRepository,
  ParsedEvent,
  WebhookRepository,
  getEventRoute,
  getEventStatus,
  getRawEventType,
  parseEvent,
} from '~/repositories';
import { BankAccountRepository } from '~/repositories/BankAccountRepository';
import { clientWrapper } from '~/repositories/client/wrapper';
import { useHelpSidebarStore } from '~/stores/HelpSidebar';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { Line } from '~/styles';

import {
  AcceptLegalAgreementModal,
  GetStarted,
  PinnedBankAccounts,
  SandboxBanner,
  TransferSnapshots,
} from './_components';

interface LocationState {
  action?: string;
}

type EventsTableRow = ParsedEvent & { action: React.ReactElement };

const StyledFade = styled(Fade)`
  margin-top: 10%;
`;

const EventsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ViewAllButtonContainer = styled.div`
  width: 100%;
  padding: 0 var(--page-margin) 24px;
  box-sizing: border-box;
`;

export const PageHomeOverview: React.FC = () => {
  const { currentUser, isAuthorized, isSandbox } = useSessionStore();
  const location = useLocation();
  const addDangerNotification = useNotificationStore((s) => s.addDangerNotification);
  const openHelpSidebar = useHelpSidebarStore((s) => s.openHelpSidebar);
  const [entities, setEntities] = useState<boolean>(false);
  const [bankAccounts, setBankAccounts] = useState<boolean>(false);
  const [webhooks, setWebhooks] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [events, setEvents] = useState<EventsTableRow[]>([]);
  const [eventsLoading, setEventsLoading] = useState<boolean>(false);
  const [isEventDetailModalOpen, setIsEventDetailModalOpen] = useState(false);
  const [selectedEventData, setSelectedEventData] = useState<any>(null);
  const { timeFormat, onTimeFormatChange } = useTimeFormat('home-events');

  const handleOpenEventDetail = useCallback((data: any) => {
    setSelectedEventData(data);
    setIsEventDetailModalOpen(true);
  }, []);

  const state = location.state as LocationState;

  const fetchData = () => {
    const promises = [];

    if (isAuthorized({ permission: 'entities', permissionLevel: 'read' })) {
      promises.push(
        EntityRepository.getAll().then((response) => {
          setEntities(response.entities.length > 0);
        })
      );
    } else {
      setEntities(false);
    }

    if (isAuthorized({ permission: 'bankAccounts', permissionLevel: 'read' })) {
      promises.push(
        BankAccountRepository.getAll().then((response) => {
          setBankAccounts(response.bankAccounts.length > 0);
        })
      );
    } else {
      setBankAccounts(false);
    }

    if (isAuthorized({ permission: 'webhooks', permissionLevel: 'read' })) {
      promises.push(
        WebhookRepository.getAll().then((response) => {
          setWebhooks(response.webhookEndpoints.length > 0);
        })
      );
    } else {
      setWebhooks(false);
    }

    clientWrapper(undefined, (e) => console.error('HomeFetchData', e), Promise.all(promises)).finally(() =>
      setLoading(false)
    );
  };

  useEffect(
    () =>
      useSessionStore.subscribe(
        (s) => [s.isLoading, s.isSandbox, s.currentUser?.defaultPlatformId],
        (s) => {
          const isLoading = s[0] as boolean | undefined;
          const defaultPlatformId = s[2] as string | undefined;

          if (isLoading || !defaultPlatformId) {
            return;
          }

          fetchData();
        },
        {
          fireImmediately: true,
          equalityFn: shallow,
        }
      ),
    []
  );

  useEffect(() => {
    if (state && state.action === 'platformInfoPermissionError') {
      addDangerNotification({
        content: "You got redirected, because you don't have permissions to view this page.",
        actionButton: {
          label: 'Roles',
          onClick: () => openHelpSidebar('Roles'),
        },
      });
    }
  }, [location]);

  useEffect(() => {
    if (!isAuthorized({ permission: 'webhooks', permissionLevel: 'read' })) {
      return;
    }

    if (!currentUser?.defaultPlatformId) {
      return;
    }

    setEventsLoading(true);
    EventRepository.getAll({ limit: 50 })
      .then((response) => {
        const entries = response.events.map(
          (event): EventsTableRow => ({
            ...parseEvent(event),
            action: (
              <div className="flex items-center justify-end">
                <JSONButton
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenEventDetail(event.data);
                  }}
                >
                  JSON
                </JSONButton>
              </div>
            ),
          })
        );
        setEvents(entries);
      })
      .catch((e) => {
        console.error('Failed to fetch events', e);
      })
      .finally(() => {
        setEventsLoading(false);
      });
  }, [currentUser?.defaultPlatformId, handleOpenEventDetail]);

  const columns: DataTableColumnDef<EventsTableRow>[] = [
    {
      header: 'Type',
      accessor: 'data',
      width: 'minmax(300px, auto)',
      cell: ({ value }) => <EventTypeCell data={value} />,
    },
    {
      header: 'Status',
      accessor: 'eventType',
      width: 'minmax(min-content, 100px)',
      cell: ({ value }) => getEventStatus(value),
    },
    getTimestampColumn<EventsTableRow>({ accessor: 'createdAt', timeFormat, onTimeFormatChange }),
    {
      header: '',
      accessor: 'action',
      width: 'min-content',
      cell: ({ value }) => value,
    },
  ];

  const navigate = useNavigate();

  return (
    <>
      <EventDetailModal
        isOpen={isEventDetailModalOpen}
        onClose={() => setIsEventDetailModalOpen(false)}
        data={selectedEventData}
      />
      <AcceptLegalAgreementModal />
      <StyledFade show={loading}>
        <LogoLoading />
      </StyledFade>
      <Fade show={!loading}>
        {isSandbox && <SandboxBanner />}

        <GetStarted
          showVerifyEmail={!currentUser?.isEmailVerified}
          showGenerateApiKeys={isAuthorized({ permission: 'apiKeys', permissionLevel: 'read' })}
          showSetupMfa={!currentUser?.factors.some((factor) => factor.factorVerified)}
          showAddBankAccount={
            !bankAccounts && entities && isAuthorized({ permission: 'bankAccounts', permissionLevel: 'read' })
          }
          showAddEntity={!entities && isAuthorized({ permission: 'entities', permissionLevel: 'read' })}
          showAddWebhook={!webhooks && isAuthorized({ permission: 'webhooks', permissionLevel: 'read' })}
          showGettingStartedGuide
        />

        <PinnedBankAccounts />

        <Line />

        <TransferSnapshots />

        {isAuthorized({ permission: 'webhooks', permissionLevel: 'read' }) && (
          <>
            <SectionHeader text="Recent Events" border />

            <EventsContainer>
              <Loading isLoading={eventsLoading}>
                <div className="px-[var(--page-margin)]">
                  <DataTable<EventsTableRow>
                    {...tableDefaults}
                    columns={columns}
                    data={events}
                    isLoading={eventsLoading}
                    rowKey={(row) => row.id}
                    emptyContent={
                      <TableEmpty
                        icon={<Icon.AnimationSignal />}
                        title="No events found"
                        description="Create your first person or a business entity."
                        action={
                          <Button
                            onClick={() => navigate(`${ROUTE.ENTITIES}/edit/person`)}
                            size="small"
                            icon={<Icon.User />}
                          >
                            Create Entity
                          </Button>
                        }
                      />
                    }
                    onRowClick={(row) =>
                      navigate(
                        getRawEventType(row.eventType) === 'Identity'
                          ? `${getEventRoute(row.eventType)}/${(row.data.subType ?? 'undefined').toLowerCase()}/${row.data.id}`
                          : getRawEventType(row.eventType) === undefined
                            ? ''
                            : `${getEventRoute(row.eventType)}/${row.data.id}`
                      )
                    }
                  />
                </div>
                {events.length > 0 && (
                  <ViewAllButtonContainer>
                    <Button variant="muted" fullWidth onClick={() => navigate(ROUTE.EVENTS)}>
                      View All Events
                    </Button>
                  </ViewAllButtonContainer>
                )}
              </Loading>
            </EventsContainer>
          </>
        )}
      </Fade>
    </>
  );
};
